import React from 'react'
import * as Immutable from 'immutable'
import styled from '@emotion/styled'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { Icon } from '@mobi/component-library/Common/Icon'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { RaceRecord, Races } from '@core/Data/Racing/races'
import { AcceptorRecord, ImageUrlWithSizes, Acceptors } from '@core/Data/Racing/acceptors'
import { MainContainer } from '@core/Components/Containers'
import { observe } from '@core/Components/HOCs/observe'
import {
  State,
  Loading,
  Errored,
  isErrored,
  isLoading,
  hasLoadedWithoutError,
  LoadingInstance,
  ErroredInstance,
} from 'rwwa-data-access'
import { guOddsAsString, OddsToggle, OddsPrice, OddsContext, OddsType } from '../Common/Odds'
import {
  GiddyUpGoodThings,
  CurrentGoodThings,
  ICurrentGoodThings,
  IGoodThingRecord,
  CurrentGoodThingsKey,
} from '../../data-access/giddyUpGoodThings'
import { LoadingMessage } from '../Common/LoadingMessage'
import { toLocalTime, formatDate, formatDateForServer } from '../../helpers'
import { DogColour } from '../Common/DogColour'
import { LastUpdated } from '../Common/LastUpdated'
import { AlertBox } from './Components/AlertBox'
import { InfoBox } from './Components/InfoBox'
import { IRaceRecord } from '../../data-access/giddyUpRaces'
import { IAcceptorRecord } from '../../data-access/giddyUpAcceptors'
import { navigateToRace } from '@classic/AppUtils/Framework/Intent/navigation'

const ItemsToDisplay = Math.min(Races.maxToKeep, Acceptors.maxToKeep)

const states$ = Rx.Observable.combineLatest(
  CurrentGoodThings.data$,
  GiddyUpGoodThings.data$,
  Races.data$,
  Acceptors.data$,
  (currentGoodThingsState, goodThingsState, racesState, acceptorsState) => ({
    currentGoodThingsState,
    goodThingsState,
    racesState,
    acceptorsState,
  })
).distinctUntilChanged(
  true,
  (x, y) =>
    Immutable.is(x.currentGoodThingsState, y.currentGoodThingsState) &&
    Immutable.is(x.goodThingsState, y.goodThingsState) &&
    Immutable.is(x.racesState, y.racesState) &&
    Immutable.is(x.acceptorsState, y.acceptorsState)
)

export const GoodThings = observe<
  {
    currentGoodThingsState: State<ICurrentGoodThings>
    goodThingsState: State<IGoodThingRecord>
    racesState: State<IRaceRecord>
    acceptorsState: State<IAcceptorRecord>
  },
  object
>(states$, ({ currentGoodThingsState, goodThingsState, racesState, acceptorsState }) => {
  const [oddsContextValue, setOddsContextValue] = React.useState<OddsType>('fixed')
  const handleToggleOddsContext = () =>
    setOddsContextValue(curr => (curr === 'fixed' ? 'tote' : 'fixed'))

  const currentGoodThingsKeys = CurrentGoodThings.getFrom(
    currentGoodThingsState,
    CurrentGoodThingsKey
  )
  const retryHandler = () =>
    CurrentGoodThings.hardInvalidate(currentGoodThingsState, CurrentGoodThingsKey)

  if (isErrored(currentGoodThingsKeys)) {
    return <AlertBox message='Unable to retrieve Good Things.' retryHandler={retryHandler} />
  }

  if (isLoading(currentGoodThingsKeys)) {
    return <LoadingMessage message='' />
  }

  if (currentGoodThingsKeys.count() === 0) {
    return <InfoBox message='No Good Things currently.' />
  }

  // ti-1180: to prevent a render/purge infinite loop, don't display more good things than the race and acceptor cache will hold.
  const goodThingsData = currentGoodThingsKeys.take(ItemsToDisplay).map(
    key =>
      !!key &&
      serialiseAccess(GiddyUpGoodThings.getFrom(goodThingsState, key), goodThing => {
        const raceMonad = Races.getFrom(racesState, goodThing.raceKey)
        const acceptorMonad = Acceptors.getFrom(acceptorsState, goodThing.key)
        return serialiseAccess(raceMonad, race =>
          serialiseAccess(acceptorMonad, acceptor => ({ race, goodThing, acceptor }))
        )
      })
  )

  const loadedGoodThingsData = goodThingsData.filter(
    data => !!data && hasLoadedWithoutError(data)
  ) as Immutable.Iterable<number, GoodThingsData>

  const updatedAt = loadedGoodThingsData.map(data => !!data && data.goodThing.updatedAt).max()
  const headerDate = loadedGoodThingsData.map(data => !!data && data.race.raceTime).min()

  return (
    <MainContainer background='surface300'>
      <PageHeader title='Good Things' />

      <OddsContext.Provider
        value={{ currentOdds: oddsContextValue, toggleOdds: handleToggleOddsContext }}
      >
        {!!headerDate && (
          <div
            className='l-faux-table w100 fz-12'
            style={{
              paddingBottom: '.5em',
              paddingTop: '.5em',
            }}
            data-tid-good-thing-header
          >
            <div className='l-faux-row'>
              <div
                className='l-faux-cell l-cell-xs-5 text-left plhalfem alignmid'
                data-tid-display-date
              >
                {formatDate(headerDate)}
              </div>

              <div className='l-faux-cell l-cell-xs-4 text-right' data-tid-odds-toggle>
                <OddsToggle />
              </div>

              <div className='l-faux-cell l-cell-xs-3 alignmid'>
                <SvgLazy name='GiddyUpLogo' height='1.4rem' color='#0051A0' />
              </div>
            </div>
          </div>
        )}

        {goodThingsData.toArray().map((data, key) => !!data && <GoodThing key={key} data={data} />)}
      </OddsContext.Provider>

      {!!updatedAt && <LastUpdated date={updatedAt} />}
    </MainContainer>
  )
})

// ================
// Local Components
// ================

const GoodThing: React.FC<{
  data: typeof LoadingInstance | typeof ErroredInstance | GoodThingsData
}> = ({ data }) => {
  if (isLoading(data)) return <GoodThingLoading />
  if (isErrored(data)) return <div />

  return <GoodThingComponent record={data as GoodThingsData} />
}

const GoodThingLoading = () => {
  return (
    <div data-tid-loading-item className='card'>
      <div
        className='fz-14 phalfem'
        style={{ padding: '1em', height: '4.571em', boxSizing: 'border-box' }}
      >
        <LoadingPlaceHolderStyled>
          <div className='backgroundmasker headertop' />
          <div className='backgroundmasker headerleft' />
          <div className='backgroundmasker headerright' />
          <div className='backgroundmasker headerbottom' />
          <div className='backgroundmasker subheaderleft' />
          <div className='backgroundmasker subheaderright' />
          <div className='backgroundmasker subheaderbottom' />
        </LoadingPlaceHolderStyled>
      </div>
    </div>
  )
}

const GoodThingComponent = ({ record }: { record: GoodThingsData }) => {
  const onClick = () =>
    navigateToRace({
      fixtureId: record.goodThing.fixtureId,
      raceNumber: `${record.goodThing.raceNumber}`,
      fixtureDate: formatDateForServer(record.goodThing.fixtureDate),
    })

  const dataTid = { [`data-tid-good-thing-${record.goodThing.key}`]: '' }

  return (
    <div data-tid-good-things className='card'>
      <GoodThingsContentStyled className='l-faux-table w100 fz-12' onClick={onClick} {...dataTid}>
        <div className='l-faux-row'>
          <GoodThingColumnOne acceptor={record.acceptor} race={record.race} />
          <GoodThingColumnTwo acceptor={record.acceptor} race={record.race} />
          <GoodThingColumnThree
            acceptor={record.acceptor}
            recommendedOdds={record.goodThing.recommendedOdds}
          />
        </div>
      </GoodThingsContentStyled>
    </div>
  )
}

const GoodThingColumnOne: React.FC<{ acceptor: AcceptorRecord; race: RaceRecord }> = ({
  acceptor,
  race,
}) => (
  <div className='l-faux-cell l-cell-xs-1 text-center aligntop'>
    <GoodThingsContentStyled className='l-faux-table'>
      <div className='l-faux-row'>
        <div className='l-faux-cell alignmid startTime' data-tid-race-time>
          {toLocalTime(race.raceTime, 'HH:mm')}
        </div>
      </div>
      <div className='l-faux-row'>
        <GoodThingSilkOrJersey
          acceptorNumber={acceptor.number}
          type={acceptor.type}
          imageUrl={acceptor.imageUrl}
          imageUrlWithSizes={acceptor.imageUrlWithSizes}
        />
      </div>
    </GoodThingsContentStyled>
  </div>
)

const GoodThingColumnTwo: React.FC<{ acceptor: AcceptorRecord; race: RaceRecord }> = ({
  acceptor,
  race,
}) => (
  <div className='l-faux-cell l-cell-xs-6 aligntop'>
    <div className='l-faux-table w100'>
      <div className='l-faux-row text-left'>
        <div className='l-faux-cell l-cell-xs-2 prhalfem' data-tid-race-type-image>
          <StarterImage starterType={acceptor.type} />
        </div>

        <div className='l-faux-cell l-cell-xs-10 faux-strong alignmid' data-tid-race-course>
          {race.meetingName}
        </div>
      </div>

      <div className='l-faux-row text-left'>
        <div className='l-faux-cell l-cell-xs-2 prhalfem' data-tid-race-number>
          R{race.raceNumber}
        </div>

        <div className='l-faux-cell l-cell-xs-10 fg-gray-base' data-tid-distance>
          {race.distance}m
        </div>
      </div>

      <div className='l-faux-row text-left'>
        <div className='l-faux-cell l-cell-xs-2 prhalfem' data-tid-acceptor-number>
          {acceptor.type === 'Dogs' ? '' : acceptor.number.toString()}
        </div>

        <div className='l-faux-cell l-cell-xs-10' data-tid-acceptor-name>
          <div style={{ wordBreak: 'break-word', hyphens: 'auto' }}>{acceptor.name}</div>
        </div>
      </div>
    </div>
  </div>
)

const GoodThingColumnThree: React.FC<{
  acceptor: AcceptorRecord
  recommendedOdds?: number
}> = ({ acceptor, recommendedOdds }) => {
  return (
    <div className='l-faux-cell l-cell-xs-5 alignmid'>
      <div className='l-faux-table w100'>
        <div className='l-faux-cell l-cell-xs-2 text-left alignmid faux-strong'>
          <OddsPrice acceptor={{ ...acceptor.toJS(), hasJumped: false }} hidePlacePrice={true} />
        </div>

        <div
          style={{ color: '#004e9d', fontWeight: 'bold' }}
          className='l-faux-cell l-cell-xs-2 text-center alignmid'
          data-tid-recommended-odds
        >
          {guOddsAsString(recommendedOdds)}
        </div>

        <div
          style={{ color: '#004e9d', fontWeight: 'bold' }}
          className='l-faux-cell l-cell-xs-1 alignmid text-right fz-18'
        >
          <span className='tticon tticon-arrowright' />
        </div>
      </div>
    </div>
  )
}

const GoodThingSilkOrJersey: React.FC<{
  acceptorNumber: number
  type: string
  imageUrl: string
  imageUrlWithSizes: ImageUrlWithSizes
}> = ({ acceptorNumber, type, imageUrl, imageUrlWithSizes }) => {
  return type === 'Dogs' ? (
    <div>
      <DogColour acceptorNumber={acceptorNumber} />
    </div>
  ) : (
    <Silk
      acceptorNumber={acceptorNumber}
      type={type}
      imageUrl={imageUrl}
      imageUrlWithSizes={imageUrlWithSizes}
    />
  )
}

const Silk: React.FC<{
  acceptorNumber: number
  type: string
  imageUrl: string
  imageUrlWithSizes: ImageUrlWithSizes
}> = ({ acceptorNumber, type, imageUrl, imageUrlWithSizes }) => {
  if (imageUrlWithSizes) {
    const isSmall = screen.width < 768
    const { width, height, y } = isSmall
      ? imageUrlWithSizes.small.size
      : imageUrlWithSizes.large.size
    const backgroundPosition = `${-width * (acceptorNumber - 1)}px ${y}px`
    const background = `url("${isSmall ? imageUrlWithSizes.small.url : imageUrlWithSizes.large.url}")`

    return (
      <div
        className='l-faux-cell'
        data-tid-silk
        style={{
          background,
          backgroundPosition,
          display: 'inline-block',
          height: height || '2.5em',
          marginTop: '.0833em', // 1px
          width: width || '2.1em',
        }}
      />
    )
  }

  const silkParams: Record<string, unknown> = {
    Races: {
      small: { width: 25, height: 30, y: 0 },
      large: { width: 35, height: 42, y: 0 },
    },
    Trots: {
      small: { width: 24, height: 18, y: -1 },
      large: { width: 36, height: 36, y: -1 },
    },
  }

  if (!silkParams[type]) return <div />

  // @ts-expect-error typing issue
  const { width, height, y } = screen.width < 768 ? silkParams[type].small : silkParams[type].large
  const backgroundPosition = `${-width * (acceptorNumber - 1)}px ${y}px`
  const background = `url("${imageUrl}&width=${width}&height=${height}")`

  return (
    <div
      className='l-faux-cell'
      data-tid-silk
      style={{
        background,
        backgroundPosition,
        display: 'inline-block',
        height: height || '2.5em',
        marginTop: '.0833em', // 1px
        width: width || '2.1em',
      }}
    />
  )
}

const StarterImage: React.FC<{ starterType: string }> = ({ starterType }) => {
  if (starterType === 'Races') {
    return <Icon type='races' />
  }

  if (starterType === 'Trots') {
    return <Icon type='trots' />
  }

  if (starterType === 'Dogs') {
    return <Icon type='dogs' />
  }

  return null
}

// =============
// Local Helpers
// =============

function serialiseAccess<T, T2>(
  obj: T | Loading | Errored,
  fn: (instance: T) => T2 | Loading | Errored
) {
  if (isLoading(obj)) {
    return obj
  } else if (isErrored(obj)) {
    return obj
  } else {
    return fn(obj)
  }
}

// ======
// Styles
// ======

const LoadingPlaceHolderStyled = styled.div({
  animationDuration: '1s',
  animationFillMode: 'forwards',
  animationIterationCount: 'infinite',
  animationName: 'placeHolderShimmer',
  animationTimingFunction: 'linear',
  background: 'linear-gradient(to right, #f6f7f8 0%, #e3e4e8 20%, #f6f7f8 40%, #f6f7f8 100%)',
  backgroundSize: '800px 104',
  height: '2.857em',
  position: 'relative',

  '@keyframes placeHolderShimmer': {
    '0%': { backgroundPosition: '-33em 0' },
    '100%': { backgroundPosition: '33em 0' },
  },

  '.backgroundmasker': {
    background: '#fff',
    position: 'absolute',

    '&.headertop': { top: 0, left: '2.857em', right: 0, height: '.571em' },
    '&.headerleft': { top: '.571em', left: '2.857em', height: '.571em', width: '.714em' },
    '&.headerbottom': { top: '1.144em', height: '.571em', left: '2.857em', right: 0 },
    '&.headerright': { top: '.571em', height: '.571em', width: 'auto', left: '21.429em', right: 0 },
    '&.subheaderleft': { top: '1.714em', left: '2.857em', height: '.571em', width: '.714em' },
    '&.subheaderright': {
      top: '1.714em',
      height: '.571em',
      width: 'auto',
      left: '14.286em',
      right: 0,
    },
    '&.subheaderbottom': { top: '2.285em', left: '2.857em', right: 0, height: '.571em' },
  },
})

const GoodThingsContentStyled = styled.div({
  borderSpacing: '.3em',
  lineHeight: 'normal',

  '.startTime': { height: '1.5em' },
  '.goodThingsDogNumber': { padding: '.25em' },
  '.acceptorAlign': { paddingLeft: '.333em' },
  '.oddspriceDisabled': { minHeight: 'auto' },
})

// =====
// Types
// =====

type GoodThingsData = {
  race: RaceRecord
  goodThing: IGoodThingRecord
  acceptor: AcceptorRecord
}
